import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { faCircle, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { ImageCropModal } from '@framework/ui/elements/ImageCropModal';
import { generateDefaultIcon } from '@framework/utils';
import { PublicIconUploader } from '@framework/utils/PublicIconUploader';
import { trpcClient } from '@framework/trpcClient';
import type { Workspace } from '@workspace/domain/workspace/Workspace';
import { useTranslation } from 'react-i18next';
import type { ModelingFormat } from 'balus-common';
import { ModelingFormatList } from 'balus-common';
import { WORKSPACE_MODELING_FORMAT_I18N_KEYS } from '@framework/i18n/modelingFormatKeys';
import TextareaAutosize from 'react-textarea-autosize';

type Props = {
  isOpen: boolean;
  onClose(): void;
  workspace: Workspace;
};

export const WorkspaceEditModal: React.FC<Props> = ({ isOpen, onClose, workspace }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>(workspace.name.toString());
  const [description, setDescription] = useState<string>(workspace.description.toString());
  const [canSave, setCanSave] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [iconImage, setIconImage] = useState<File | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(workspace.imageUrl);
  const [modelingFormat, setModelingFormat] = useState(workspace.setting.modelingFormat);

  const handleUploadImage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) setUploadedImage(event.target.files[0]);
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const newIconUrl = iconImage ? await PublicIconUploader.uploadWorkspaceIcon(workspace.id, iconImage) : iconUrl;
      setIconUrl(newIconUrl);
      await trpcClient.workspace.updateWorkspaceEntity.mutate({
        workspaceId: workspace.id,
        name,
        description,
        imageUrl: newIconUrl,
        modelingFormat,
      });
      onClose();
    } catch (e) {
      console.error(e);
      setErrorMessage(t('workspace.edit.updateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCropModalClose = useCallback(() => {
    (document.getElementById('icon-image') as HTMLInputElement).value = '';
    setUploadedImage(null);
    setIsCropModalOpen(false);
  }, []);

  const handleCropModalSubmit = useCallback((image: File) => {
    setIconImage(image);
    setIconUrl(URL.createObjectURL(image));
    setIsCropModalOpen(false);
  }, []);

  const handleImageClear = useCallback(() => {
    setIconImage(null);
    setIconUrl(null);
  }, []);

  useEffect(() => {
    setCanSave(name !== '');
  }, [name, description]);

  useEffect(() => {
    if (uploadedImage) setIsCropModalOpen(true);
  }, [uploadedImage]);

  return (
    <>
      <GeneralModal
        isOpen={isOpen}
        onClose={onClose}
        title={t('workspace.edit.title')}
        submitButton={
          <Button color="primary" onClick={handleSubmit} disabled={!canSave} loading={isSubmitting}>
            {t('common.action.save')}
          </Button>
        }
        cancelButton={
          <Button color={'secondary'} onClick={onClose} disabled={isSubmitting}>
            {t('common.action.cancel')}
          </Button>
        }
        // オーバーレイ部分をクリックして閉じると、書き換えの説明が消えて悲しいことになりかねないので、モーダルを閉じないようにする
        shouldCloseOnOverlayClick={false}
      >
        <div className="overflow-y-auto p-4" style={{ width: '600px', maxHeight: '70vh' }}>
          <div className="flex justify-between">
            {iconUrl ? (
              <div className="relative size-16">
                <div className="absolute size-16 overflow-hidden rounded-full">
                  <img className="size-full" src={iconUrl} alt={name} />
                </div>
                <div className="absolute flex size-16 items-start justify-end">
                  <div className="relative flex cursor-pointer items-center justify-center" onClick={handleImageClear}>
                    <FontAwesomeIcon icon={faCircle} className="absolute text-2xl text-red-500" />
                    <FontAwesomeIcon icon={faTimes} className="absolute text-xl text-white" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative size-16 overflow-hidden rounded-full">
                <div className="absolute size-full opacity-50">
                  {generateDefaultIcon({
                    id: workspace.id,
                    name: workspace.name.toString(),
                    size: 64,
                  })}
                </div>
                <div className="absolute flex size-full items-center justify-center">
                  <FontAwesomeIcon icon={faPlus} className="text-2xl text-blue-500" />
                </div>
                <input
                  type="file"
                  id="icon-image"
                  className="absolute size-full cursor-pointer opacity-0"
                  accept="image/png, image/jpeg"
                  onChange={handleUploadImage}
                />
              </div>
            )}
            <div className="w-5/6">
              <div className="text-lg font-bold">{t('workspace.edit.form.nameLabel')}</div>
              <Input
                type="text"
                placeholder={t('workspace.edit.form.namePlaceholder')}
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                disabled={isSubmitting}
                autoFocus={true}
              />
            </div>
          </div>

          <div className="py-2">
            <div className="text-lg font-bold">{t('workspace.edit.form.descriptionLabel')}</div>
            <TextareaAutosize
              className="w-full rounded border border-gray-400 bg-gray-100 p-1"
              onChange={(e) => setDescription(e.target.value)}
              value={description}
              minRows={5}
              maxRows={20}
              disabled={isSubmitting}
            />
          </div>

          {/* モデリングフォーマット設定 */}
          <div className="pb-2 pt-8 font-bold">{t('workspace.edit.modelingFormat.title')}</div>
          <div>
            <div>
              <select
                className="form-select m-1 py-1 text-sm"
                value={modelingFormat}
                onChange={(e) => setModelingFormat(e.target.value as ModelingFormat)}
              >
                {ModelingFormatList.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(WORKSPACE_MODELING_FORMAT_I18N_KEYS[mode])}
                  </option>
                ))}
              </select>
            </div>

            <div>{t('workspace.edit.modelingFormat.note')}</div>
          </div>

          {errorMessage && <div className="py-2 text-red-500">{errorMessage}</div>}
        </div>
      </GeneralModal>
      {uploadedImage && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={handleCropModalClose}
          onSubmit={handleCropModalSubmit}
          src={uploadedImage}
        />
      )}
    </>
  );
};
